import React, { useState, useEffect } from 'react';
import { encodeBase64, decodeBase64 } from '../base64';

const owner = process.env.REACT_APP_GITHUB_OWNER;
const repo = process.env.REACT_APP_GITHUB_REPO;
const branch = process.env.REACT_APP_GITHUB_BRANCH || 'main';
const token = process.env.REACT_APP_GITHUB_TOKEN;

async function getFile(path) {
  const res = await fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}?ref=${branch}`, {
    headers: { Authorization: `token ${token}` }
  });
  if (!res.ok) return null;
  return res.json();
}

async function updateFile(path, content, message, sha) {
  return fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `token ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ message, content, branch, sha })
    });
}

async function deleteFile(path, message, sha) {
  return fetch(`https://api.github.com/repos/${owner}/${repo}/contents/${path}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `token ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ message, sha, branch })
    });
}

function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-');
}

function safeFilename(name) {
  const parts = name.split('.');
  const ext = parts.pop();
  return `${slugify(parts.join('.'))}.${ext}`;
}

async function cleanupUnusedEventImages(events) {
  try {
    const dir = await getFile('public/events');
    if (!Array.isArray(dir)) return;
    const slugs = new Set(events.map((e) => e.slug));
    for (const item of dir) {
      if (item.type === 'dir' && !slugs.has(item.name)) {
        const files = await getFile(item.path);
        if (Array.isArray(files)) {
          for (const f of files) {
            await deleteFile(f.path, `Remove unused image folder ${item.name}`, f.sha);
          }
        }
      }
    }
  } catch (err) {
    console.error(err);
  }
}

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

function EventForm({ existing, onClose }) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showEndDate, setShowEndDate] = useState(false);
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [draft, setDraft] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setDate(existing.date);
      setEndDate(existing.endDate || '');
      setShowEndDate(!!existing.endDate);
      setDescription(existing.description);
      setDraft(!!existing.draft);
      setPreviewUrl(existing.imageUrl || '');
    }
  }, [existing]);

  useEffect(() => {
    return () => {
      if (imageFile && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [imageFile, previewUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    if (imageFile && imageFile.size > MAX_FILE_SIZE) {
      setError('Image must be less than 1MB');
      setSaving(false);
      return;
    }
    const id = existing ? existing.id : Date.now();
    const slug = `${slugify(title)}-${id}`;
    let imageUrl = existing ? existing.imageUrl : '';

    if (imageFile) {
      const safeName = safeFilename(imageFile.name);
      const imagePath = `public/events/${slug}/${safeName}`;
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        await updateFile(imagePath, base64Data, `Add image ${id} [skip netlify]`);
        imageUrl = `/${imagePath.replace('public/', '')}`;
        if (existing && existing.imageUrl && existing.imageUrl !== imageUrl) {
          const oldPath = `public${existing.imageUrl}`;
          const oldFile = await getFile(oldPath);
          if (oldFile) {
            await deleteFile(oldPath, `Delete image ${id} [skip netlify]`, oldFile.sha);
          }
        }
        await saveEvent(id, slug, title, date, endDate, description, imageUrl);
      };
      reader.readAsDataURL(imageFile);
    } else {
      await saveEvent(id, slug, title, date, endDate, description, imageUrl);
    }
  };

  async function saveEvent(id, slug, title, date, endDate, description, imageUrl) {
    const file = await getFile('events.json');
    let events = [];
    let sha;
    if (file) {
      events = JSON.parse(decodeBase64(file.content));
      sha = file.sha;
    }
    const existingIndex = events.findIndex((e) => e.id === id);
    const eventData = {
      ...(existingIndex >= 0 ? events[existingIndex] : {}),
      id,
      slug,
      title,
      date,
      endDate: showEndDate ? endDate : '',
      description,
      imageUrl,
      draft
    };
    if (existingIndex >= 0) {
      events[existingIndex] = eventData;
    } else {
      events.push(eventData);
    }
    const newContent = encodeBase64(JSON.stringify(events, null, 2));
    await updateFile('events.json', newContent, `Update events ${id}`, sha);
    await cleanupUnusedEventImages(events);
    setSaving(false);
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-20">
      <form className="bg-white p-4 flex flex-col gap-2 w-96" onSubmit={handleSubmit}>
        <h2 className="text-xl mb-2">{existing ? 'Edit Event' : 'Add Event'}</h2>
        <input className="border p-2" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" />
        <input type="datetime-local" className="border p-2" value={date} onChange={(e) => setDate(e.target.value)} />
        {showEndDate ? (
          <input
            type="datetime-local"
            className="border p-2"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        ) : (
          <button
            type="button"
            className="text-sm underline text-left"
            onClick={() => setShowEndDate(true)}
          >
            Add end date/time
          </button>
        )}
        <textarea className="border p-2" rows="4" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" />
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft}
            onChange={(e) => setDraft(e.target.checked)}
          />
          Draft (visible only when logged in)
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files[0];
            if (file && file.size > MAX_FILE_SIZE) {
              setError('Image must be less than 1MB');
              setImageFile(null);
              setPreviewUrl(existing ? existing.imageUrl || '' : '');
              e.target.value = null;
            } else {
              setError('');
              setImageFile(file || null);
              if (file) {
                setPreviewUrl(URL.createObjectURL(file));
              } else {
                setPreviewUrl(existing ? existing.imageUrl || '' : '');
              }
            }
          }}
        />
        {previewUrl && (
          <img src={previewUrl} alt="preview" className="w-20 h-20 object-cover" />
        )}
        {error && <div className="text-red-500 text-sm">{error}</div>}
        <div className="flex gap-2 mt-2">
          <button type="submit" className="bg-black text-white p-2" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          <button type="button" className="p-2" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

export default EventForm;
